<template>
    <div class="sensitive-words-container">
        <div class="page-header">
            <h2>敏感词管理</h2>
            <div class="header-actions">
                <el-input v-model="searchQuery" placeholder="搜索敏感词..." class="search-input" clearable
                    @clear="fetchWords" @keyup.enter="fetchWords">
                    <template #append>
                        <el-button icon="Search" @click="fetchWords" />
                    </template>
                </el-input>
                <el-button type="primary" icon="Plus" @click="showAddDialog">添加敏感词</el-button>
            </div>
        </div>

        <div class="stats-bar">
            <el-tag type="info" size="large">共 {{ total }} 个敏感词</el-tag>
        </div>

        <el-table v-loading="loading" :data="words" style="width: 100%" border stripe>
            <el-table-column label="ID" prop="id" width="80" />
            <el-table-column label="敏感词" prop="word" min-width="200">
                <template #default="{ row }">
                    <el-tag type="danger" effect="plain">{{ row.word }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column label="分类" prop="category" width="120">
                <template #default="{ row }">
                    <el-tag v-if="row.category" :type="getCategoryTagType(row.category)">{{ row.category }}</el-tag>
                    <span v-else class="text-muted">未分类</span>
                </template>
            </el-table-column>
            <el-table-column label="添加时间" width="180">
                <template #default="{ row }">
                    {{ formatDate(row.createdAt) }}
                </template>
            </el-table-column>
            <el-table-column label="操作" width="100" fixed="right">
                <template #default="{ row }">
                    <el-popconfirm title="确定要删除该敏感词吗？" @confirm="deleteWord(row.id)">
                        <template #reference>
                            <el-button type="danger" link>删除</el-button>
                        </template>
                    </el-popconfirm>
                </template>
            </el-table-column>
        </el-table>

        <div class="pagination-container">
            <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize" :total="total"
                layout="total, prev, pager, next" @current-change="fetchWords" />
        </div>

        <!-- 添加敏感词对话框 -->
        <el-dialog v-model="addDialogVisible" title="添加敏感词" width="500px">
            <el-form :model="addForm" label-position="top">
                <el-form-item label="敏感词">
                    <el-input v-model="addForm.words" type="textarea" :rows="5"
                        placeholder="输入敏感词，支持逗号、中文逗号或换行分隔多个词。&#10;例如：&#10;词1，词2，词3&#10;或每行一个" />
                </el-form-item>
                <el-form-item label="分类">
                    <el-select v-model="addForm.category" placeholder="选择分类" clearable allow-create filterable>
                        <el-option label="政治" value="政治" />
                        <el-option label="色情" value="色情" />
                        <el-option label="辱骂" value="辱骂" />
                        <el-option label="广告" value="广告" />
                        <el-option label="违法" value="违法" />
                        <el-option label="其他" value="其他" />
                    </el-select>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="addDialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="addLoading" @click="addWords">确定添加</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import api from '@/utils/api'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'

const loading = ref(false)
const words = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(20)
const searchQuery = ref('')

const addDialogVisible = ref(false)
const addLoading = ref(false)
const addForm = reactive({
    words: '',
    category: ''
})

const fetchWords = async () => {
    loading.value = true
    try {
        const params = {
            page: currentPage.value,
            page_size: pageSize.value,
            keyword: searchQuery.value
        }
        const response = await api.get('/api/admin/sensitive-words', { params })
        words.value = response.data.words || []
        total.value = response.data.total
    } catch (error) {
        ElMessage.error('获取敏感词列表失败')
    } finally {
        loading.value = false
    }
}

const showAddDialog = () => {
    addForm.words = ''
    addForm.category = ''
    addDialogVisible.value = true
}

const addWords = async () => {
    if (!addForm.words.trim()) {
        ElMessage.warning('请输入敏感词')
        return
    }
    addLoading.value = true
    try {
        const response = await api.post('/api/admin/sensitive-words', {
            words: addForm.words,
            category: addForm.category
        })
        ElMessage.success(`添加成功：新增 ${response.data.added} 个，跳过 ${response.data.skipped} 个（已存在）`)
        addDialogVisible.value = false
        fetchWords()
    } catch (error) {
        ElMessage.error('添加失败')
    } finally {
        addLoading.value = false
    }
}

const deleteWord = async (id) => {
    try {
        await api.delete(`/api/admin/sensitive-words/${id}`)
        ElMessage.success('删除成功')
        fetchWords()
    } catch (error) {
        ElMessage.error('删除失败')
    }
}

const getCategoryTagType = (category) => {
    const map = {
        '政治': 'danger',
        '色情': 'danger',
        '辱骂': 'warning',
        '广告': 'info',
        '违法': 'danger',
        '其他': ''
    }
    return map[category] || ''
}

const formatDate = (date) => {
    return dayjs(date).format('YYYY-MM-DD HH:mm')
}

onMounted(() => {
    fetchWords()
})
</script>

<style scoped>
.sensitive-words-container {
    padding: 20px;
    background: #fff;
    border-radius: 8px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
}

.search-input {
    width: 250px;
}

.stats-bar {
    margin-bottom: 16px;
}

.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

.text-muted {
    color: #909399;
    font-size: 13px;
}
</style>
